import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * NameDialog — the small "type a name" dialog.
 *
 * Two uses, deliberately one component so they look identical:
 *  - **Rename** a trip or a journey (from the ⋮ kebab menus).
 *  - **Create copy**, where the field arrives pre-filled with "Copy of {name}".
 *
 * Matches ConfirmationDialog's overlay (portal, z-index 3000, backdrop click to
 * cancel, Escape to cancel) so the two read as one family — see
 * docs/codebase/ui-conventions.md §Dialogs.
 *
 * The input is focused and fully selected on open, so the common case (type a
 * whole new name) is one keystroke away, and Enter submits.
 */
interface NameDialogProps {
  title: string;
  /** Field label above the input, e.g. "Trip name". */
  label: string;
  /** Starting value; selected on open so it can be typed over. */
  initialValue: string;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
  /** Disables the form while the caller persists the change. */
  busy?: boolean;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

const NameDialog: React.FC<NameDialogProps> = ({
  title,
  label,
  initialValue,
  confirmText = 'Save',
  cancelText = 'Cancel',
  placeholder,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus + select so the prefilled name (a title, or "Copy of X") can be
    // replaced by typing, or kept by pressing Enter.
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0 && !busy;

  const submit = () => { if (canSubmit) onConfirm(trimmed); };

  const dialog = (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3000,
      }}
      onClick={() => { if (!busy) onCancel(); }}
    >
      <form
        role="dialog"
        aria-labelledby="name-dialog-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onCancel(); }}
        style={{
          backgroundColor: 'white', borderRadius: 8, padding: '2rem',
          width: '90%', maxWidth: 400, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        <h2 id="name-dialog-title" style={{ margin: '0 0 1rem', fontSize: 18, fontWeight: 700, color: '#16241d' }}>
          {title}
        </h2>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#6c7872', marginBottom: 6 }}>
          {label}
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            disabled={busy}
            style={{
              display: 'block', width: '100%', marginTop: 6, padding: '9px 12px',
              borderRadius: 12, border: '1.5px solid rgba(20,40,30,0.18)',
              fontSize: 14, color: '#16241d', boxSizing: 'border-box',
            }}
          />
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.5rem' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '9px 14px', borderRadius: 12, border: 'none',
              background: '#f4f6f3', color: '#6c7872', fontSize: 13, fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            {cancelText}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              padding: '9px 14px', borderRadius: 12, border: 'none',
              background: '#157a55', color: '#ffffff', fontSize: 13, fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'default', opacity: canSubmit ? 1 : 0.5,
            }}
          >
            {busy ? 'Saving…' : confirmText}
          </button>
        </div>
      </form>
    </div>
  );

  return createPortal(dialog, document.body);
};

export default NameDialog;
